import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Mic, MicOff, Video, VideoOff, MonitorUp, StickyNote,
  PhoneOff, MessageSquare, Send, Eye, EyeOff,
} from 'lucide-react';
import Peer from 'peerjs';
import { useNotification } from '../../notification/NotificationContext';
import {
  registerPeer, getPeer, deletePeer, deleteRoom, getMeetingAccess,
} from '../../services/mockInterviewService';
import { extractUserId } from '../../utils/auth';
import '../../components/MockInterview/MockInterview.css';

// Safari-safe gUM: full HD constraints first, then fall back to plain video+audio
// before surfacing a permission error to the user.
async function getLocalStream() {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        width:  { ideal: 1280, max: 1920 },
        height: { ideal: 720,  max: 1080 },
        aspectRatio: { ideal: 16 / 9 },
        facingMode: 'user',
        frameRate: { ideal: 30, max: 60 },
      },
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (err) {
    try {
      return await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch (err2) {
      const e = new Error(
        'Camera/microphone access denied. ' +
        'On iOS: Settings → Safari → Camera & Microphone → Allow. ' +
        'On Mac: System Preferences → Security → Camera → allow browser.'
      );
      e.cause = err2;
      throw e;
    }
  }
}

// iOS Safari refuses to autoplay even with the autoplay attribute unless we
// call play() ourselves once srcObject is set. Doing this for every srcObject
// assignment makes desktop Chrome happy too.
function attachStream(videoEl, stream) {
  if (!videoEl || !stream) return;
  videoEl.srcObject = stream;
  videoEl.onloadedmetadata = () => {
    videoEl.play().catch(() => { /* autoplay blocked — ignored */ });
  };
}

// STUN + open TURN. The TURN servers are the operative bit for iOS/Mac users
// behind symmetric NAT — without them PeerJS sits forever on "connecting".
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'turn:openrelay.metered.ca:80',  username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
];

// PeerJS-based 1-on-1 video room.
// Each participant registers a stable peer ID derived from userId+roomId so a
// browser refresh re-binds to the same identity. Whoever opens first becomes
// the "waiter"; the second participant calls them. We poll the meeting-peer
// endpoint until the other side appears.
const MeetingRoomPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [status, setStatus] = useState('connecting');   // connecting | waiting | live | error
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [hideSelfView, setHideSelfView] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [notes, setNotes] = useState('');
  const [access, setAccess] = useState(null); // { currentUserName, otherParticipantName, isInterviewer }
  const [chatReady, setChatReady] = useState(false);
  const [remoteCameraOn, setRemoteCameraOn] = useState(true);
  const [remoteHasVideo, setRemoteHasVideo] = useState(false);
  const [permissionError, setPermissionError] = useState('');

  // Chat state — backed by PeerJS DataConnection; no extra backend.
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const dataConnRef = useRef(null);

  const peerRef = useRef(null);
  const callRef = useRef(null);
  const localStreamRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const activeStreamRef = useRef(null);   // current outgoing stream (camera or screen)
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pollTimerRef = useRef(null);
  const accessRef = useRef(null);
  const sharingRef = useRef(false);

  const notesKey = `meeting-notes-${roomId}`;

  // Notes — persisted per-room in localStorage so they survive a refresh.
  useEffect(() => {
    setNotes(localStorage.getItem(notesKey) || '');
  }, [notesKey]);
  useEffect(() => {
    const id = setTimeout(() => localStorage.setItem(notesKey, notes), 300);
    return () => clearTimeout(id);
  }, [notes, notesKey]);

  // Main connection lifecycle — runs once per roomId.
  useEffect(() => {
    let cancelled = false;
    // Stable per-user peer id — survives refresh, lets the other side find us
    // again at the same address after a reload.
    const token = localStorage.getItem('token');
    const userId = extractUserId(token) || 'anon';
    const stablePeerId = `mi-${roomId}-${userId}`.replace(/[^a-zA-Z0-9-]/g, '-');

    const attachRemote = (stream) => {
      if (cancelled) return;
      if (remoteVideoRef.current) {
        attachStream(remoteVideoRef.current, stream);
        remoteVideoRef.current.muted = false;
        remoteVideoRef.current.volume = 1.0;
      }
      const v = stream.getVideoTracks()[0];
      setRemoteHasVideo(!!v);
      if (v) {
        // Mirror the track's enabled state into the avatar fallback.
        setRemoteCameraOn(v.enabled);
        v.onmute = () => setRemoteCameraOn(false);
        v.onunmute = () => setRemoteCameraOn(true);
      }
      setStatus('live');
    };

    const wireDataConn = (dc) => {
      dataConnRef.current = dc;
      setChatReady(true);
      dc.on('data', (data) => {
        if (cancelled) return;
        // Try JSON first for control messages (camera-toggle); fall back to chat text.
        if (typeof data === 'string' && data.startsWith('{')) {
          try {
            const msg = JSON.parse(data);
            if (msg && msg.type === 'camera-toggle') {
              setRemoteCameraOn(!!msg.on);
              return;
            }
            if (msg && msg.type === 'chat' && typeof msg.text === 'string') {
              const fromName = accessRef.current?.otherParticipantName || 'Peer';
              setMessages((prev) => [...prev, { from: fromName, text: msg.text, time: new Date() }]);
              return;
            }
          } catch { /* fall through to plain text */ }
        }
        const text = typeof data === 'string' ? data : String(data ?? '');
        const fromName = accessRef.current?.otherParticipantName || 'Peer';
        setMessages((prev) => [...prev, { from: fromName, text, time: new Date() }]);
      });
      dc.on('close', () => {
        if (dataConnRef.current === dc) dataConnRef.current = null;
        setChatReady(false);
      });
    };

    const callPeer = (otherPeerId, peer) => {
      // Use the current outgoing stream — if the user is already screen-sharing
      // when the peer (re)connects, they keep seeing the screen, not a black
      // frame from the camera-only initial stream.
      const outgoing = activeStreamRef.current || localStreamRef.current;
      const call = peer.call(otherPeerId, outgoing);
      callRef.current = call;
      call.on('stream', attachRemote);
      call.on('close', () => !cancelled && setStatus('waiting'));

      // Open a side-channel for chat. Caller initiates; callee receives via
      // peer.on('connection') in the main setup.
      try {
        const dc = peer.connect(otherPeerId, { reliable: true });
        dc.on('open', () => wireDataConn(dc));
      } catch (e) {
        // Non-fatal — chat just won't work, video still does.
      }
    };

    const pollForOther = (peer) => {
      pollTimerRef.current = setInterval(async () => {
        try {
          const other = await getPeer(roomId);
          if (cancelled) return;
          if (other?.peerId && other.peerId !== peer.id) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            setStatus('connecting');
            callPeer(other.peerId, peer);
          }
        } catch { /* keep polling */ }
      }, 3000);
    };

    const start = async () => {
      try {
        // 0) Access gate — also gives us participant names.
        const acc = await getMeetingAccess(roomId).catch(() => null);
        if (cancelled) return;
        if (acc && acc.hasAccess === false) {
          showNotification('You do not have access to this meeting', 'error');
          navigate('/mock-interview/book');
          return;
        }
        if (acc) { setAccess(acc); accessRef.current = acc; }

        // 1) Local mic + camera with Safari-safe constraints + manual play().
        const local = await getLocalStream();
        if (cancelled) { local.getTracks().forEach((t) => t.stop()); return; }
        localStreamRef.current = local;
        activeStreamRef.current = local;
        cameraTrackRef.current = local.getVideoTracks()[0] || null;
        attachStream(localVideoRef.current, local);

        // 2) Spin up our PeerJS peer with a deterministic id + STUN/TURN.
        const peer = new Peer(stablePeerId, {
          config: { iceServers: ICE_SERVERS },
          debug: 1,
        });
        peerRef.current = peer;

        peer.on('error', (err) => {
          // 'unavailable-id' simply means we are reconnecting after a refresh
          // and the broker still has the old id; PeerJS will reuse it.
          if (err?.type === 'unavailable-id') return;
          console.error('Peer error:', err);
          if (!cancelled) {
            showNotification(err.message || 'Connection error', 'error');
            setStatus('error');
          }
        });

        peer.on('open', async (myId) => {
          try {
            // Always (re)register so the backend has the freshest mapping.
            await registerPeer({ roomId, peerId: myId });
            if (cancelled) return;

            const existing = await getPeer(roomId);
            if (cancelled) return;

            if (existing?.peerId && existing.peerId !== myId) {
              setStatus('connecting');
              callPeer(existing.peerId, peer);
            } else {
              setStatus('waiting');
              pollForOther(peer);
            }
          } catch (e) {
            if (!cancelled) {
              showNotification(e.message || 'Failed to join room', 'error');
              setStatus('error');
            }
          }
        });

        // Receive incoming media call (we are the waiter).
        peer.on('call', (call) => {
          callRef.current = call;
          // Answer with whatever stream we are currently sending — this is
          // critical when the other side reconnects mid-screen-share.
          call.answer(activeStreamRef.current || localStreamRef.current);
          call.on('stream', attachRemote);
          call.on('close', () => !cancelled && setStatus('waiting'));
        });

        // Receive incoming chat data-channel.
        peer.on('connection', (dc) => {
          dc.on('open', () => wireDataConn(dc));
        });
      } catch (e) {
        if (!cancelled) {
          const msg = e.message || 'Could not access camera/mic';
          setPermissionError(msg);
          showNotification(msg, 'error');
          setStatus('error');
        }
      }
    };

    start();

    const onUnload = () => { try { deletePeer(roomId); } catch {} };
    window.addEventListener('beforeunload', onUnload);

    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', onUnload);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      try { dataConnRef.current?.close(); } catch {}
      try { callRef.current?.close(); } catch {}
      try { peerRef.current?.destroy(); } catch {}
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      // Best-effort graceful leave; backend treats 404 as no-op.
      deletePeer(roomId).catch(() => {});
      dataConnRef.current = null;
      callRef.current = null;
      peerRef.current = null;
      localStreamRef.current = null;
    };
  }, [roomId, showNotification, navigate]);

  const toggleMute = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  const toggleVideo = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !videoOff;
    stream.getVideoTracks().forEach((t) => { t.enabled = !next; });
    setVideoOff(next);
    // Tell the peer so they can swap to an avatar without waiting on muted/unmuted track events.
    try {
      dataConnRef.current?.send(JSON.stringify({ type: 'camera-toggle', on: !next }));
    } catch { /* non-fatal */ }
  };

  // Replace the outgoing video track with a screen-share track. Reverts
  // to the camera when the user stops sharing.
  const toggleShare = async () => {
    const sender = callRef.current?.peerConnection
      ?.getSenders().find((s) => s.track && s.track.kind === 'video');

    if (!sharing) {
      try {
        const display = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always',
            displaySurface: 'monitor',
            width:  { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
          },
          audio: false,
        });
        const screenTrack = display.getVideoTracks()[0];
        if (sender) await sender.replaceTrack(screenTrack);
        attachStream(localVideoRef.current, display);
        // Track the screen as the active outgoing stream so any
        // mid-share reconnect/refresh answers with the screen feed.
        activeStreamRef.current = display;
        sharingRef.current = true;
        setSharing(true);
        setHideSelfView(true);
        screenTrack.onended = async () => {
          const s = callRef.current?.peerConnection
            ?.getSenders().find((x) => x.track && x.track.kind === 'video');
          if (s && cameraTrackRef.current) await s.replaceTrack(cameraTrackRef.current);
          attachStream(localVideoRef.current, localStreamRef.current);
          activeStreamRef.current = localStreamRef.current;
          sharingRef.current = false;
          setSharing(false);
          setHideSelfView(false);
        };
      } catch (e) {
        showNotification(e.message || 'Screen share cancelled', 'warning');
      }
    } else {
      if (sender && cameraTrackRef.current) await sender.replaceTrack(cameraTrackRef.current);
      attachStream(localVideoRef.current, localStreamRef.current);
      activeStreamRef.current = localStreamRef.current;
      sharingRef.current = false;
      setSharing(false);
      setHideSelfView(false);
    }
  };

  const sendChat = () => {
    const dc = dataConnRef.current;
    const text = chatInput.trim();
    if (!dc || !text) return;
    try { dc.send(JSON.stringify({ type: 'chat', text })); } catch { return; }
    setMessages((prev) => [...prev, { from: 'You', text, time: new Date() }]);
    setChatInput('');
  };

  const leave = async () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    try { dataConnRef.current?.close(); } catch {}
    try { callRef.current?.close(); } catch {}
    try { peerRef.current?.destroy(); } catch {}
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    // Host-style cleanup: drop peer entry; only host (interviewer) tears down room.
    try { await deletePeer(roomId); } catch {}
    if (accessRef.current?.isInterviewer) {
      try { await deleteRoom(roomId); } catch {}
    }
    navigate('/mock-interview/book');
  };

  const statusLabel =
    status === 'live'    ? 'Live' :
    status === 'waiting' ? 'Waiting for peer…' :
    status === 'error'   ? 'Connection error' :
                           'Connecting…';
  const statusClass =
    status === 'live'   ? 'mr-status-live' :
    status === 'error'  ? 'mr-status-error' :
                          'mr-status-wait';

  const remoteName  = access?.otherParticipantName || 'Other participant';
  const remoteRole  = access ? (access.isInterviewer ? 'Candidate' : 'Interviewer') : '';
  const sideOpen    = showNotes || showChat;
  const localName   = access?.currentUserName || 'You';

  const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]).join('').toUpperCase() || '?';
  };
  const showRemoteAvatar = status === 'live' && (!remoteHasVideo || !remoteCameraOn);

  if (permissionError) {
    return (
      <div className="mr-shell">
        <div className="mr-permission-overlay">
          <div className="mr-permission-card">
            <span className="mr-permission-icon" role="img" aria-label="camera">📷</span>
            <h2>Camera Access Required</h2>
            <p>
              <strong>iOS Safari:</strong> Settings → Safari → Camera → Allow<br />
              <strong>Mac Safari:</strong> Safari menu → Settings for This Website → Camera → Allow<br />
              <strong>Chrome iOS:</strong> Tap the lock icon in address bar → Allow Camera
            </p>
            <div className="mr-permission-actions">
              <button className="mr-btn-primary" onClick={() => window.location.reload()}>
                Try Again
              </button>
              <button className="mr-btn-secondary" onClick={() => navigate('/mock-interview/book')}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mr-shell">
      <div className="mr-bar">
        <div className="mr-brand-wrap">
          <div className="mr-brand-mark">S</div>
          <div>
            <div className="mr-brand">SkillOn 1-on-1</div>
            <div className="mr-room">Room {roomId?.slice(0, 8)}</div>
          </div>
        </div>
        <div className={`mr-status ${statusClass}`}>
          <span className="mr-status-dot" />
          {statusLabel}
        </div>
      </div>

      <div className="mr-main" style={!sideOpen ? { gridTemplateColumns: '1fr' } : undefined}>
        <div className="mr-stage">
          {sharing && (
            <div className="mr-share-banner">
              <span className="mr-share-dot" />
              <span>You are sharing your screen</span>
              <button type="button" onClick={toggleShare}>Stop sharing</button>
              <button type="button" onClick={() => setHideSelfView((v) => !v)}>
                {hideSelfView ? 'Show preview' : 'Hide preview'}
              </button>
            </div>
          )}
          <video
            ref={remoteVideoRef}
            className="mr-remote"
            autoPlay
            playsInline
            style={showRemoteAvatar ? { visibility: 'hidden' } : undefined}
          />
          {showRemoteAvatar && (
            <div className="mr-remote-avatar" style={{ position: 'absolute', inset: 0 }}>
              <div className="mr-remote-avatar-circle">{initials(remoteName)}</div>
              <div className="mr-remote-avatar-name">{remoteName}</div>
            </div>
          )}
          {status === 'live' && (
            <div className="mr-name-overlay mr-name-remote">
              {remoteName}{remoteRole ? ` (${remoteRole})` : ''}
            </div>
          )}
          {status !== 'live' && (
            <div className="mr-remote-empty" style={{ position: 'absolute' }}>
              <div className="mr-wait-card">
                <div className="mr-wait-orbit">
                  <span>{initials(remoteName)}</span>
                </div>
                <h2>{statusLabel}</h2>
                <p>
                  {status === 'waiting'
                    ? `${remoteName} can join with this room link.`
                    : 'Setting up camera, microphone, and connection.'}
                </p>
              </div>
            </div>
          )}
          <div className={`mr-local ${hideSelfView ? 'mr-local-hidden' : ''}`}>
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              style={videoOff && !sharing ? { visibility: 'hidden' } : undefined}
            />
            {videoOff && !sharing && (
              <div className="mr-local-avatar" style={{ position: 'absolute', inset: 0 }}>
                <div className="mr-local-avatar-circle">{initials(localName)}</div>
              </div>
            )}
            <div className="mr-name-overlay mr-name-local">You</div>
          </div>
          {hideSelfView && (
            <button className="mr-local-restore" type="button" onClick={() => setHideSelfView(false)}>
              <Eye size={14} />
              Show preview
            </button>
          )}
        </div>

        {sideOpen && (
          <aside className="mr-side">
            <div className="mr-side-tabs">
              <button
                className={`mr-side-tab ${showNotes ? 'mr-side-tab-active' : ''}`}
                onClick={() => { setShowNotes(true); setShowChat(false); }}
              >Notes</button>
              <button
                className={`mr-side-tab ${showChat ? 'mr-side-tab-active' : ''}`}
                onClick={() => { setShowChat(true); setShowNotes(false); }}
              >Chat</button>
            </div>

            {showNotes && (
              <>
                <textarea
                  className="mr-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Jot down questions, observations, follow-ups…"
                />
                <div className="mr-side-foot">Auto-saved locally</div>
              </>
            )}

            {showChat && (
              <>
                <div className="mr-chat-list">
                  {messages.length === 0 && (
                    <div style={{ opacity: 0.5, fontSize: 12, padding: 12 }}>
                      {chatReady ? 'Say hello…' : 'Chat opens once both participants connect.'}
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className="mr-chat-msg">
                      <div className="mr-chat-msg-head">
                        <span className="mr-chat-from">{m.from}</span>
                        <span className="mr-chat-time">
                          {m.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="mr-chat-text">{m.text}</div>
                    </div>
                  ))}
                </div>
                <div className="mr-chat-compose">
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendChat()}
                    placeholder={chatReady ? 'Type a message…' : 'Waiting for peer to connect…'}
                    disabled={!chatReady}
                  />
                  <button onClick={sendChat} disabled={!chatReady || !chatInput.trim()}>
                    <Send size={16} />
                  </button>
                </div>
              </>
            )}
          </aside>
        )}
      </div>

      <div className="mr-toolbar">
        <button className={`mr-tool ${muted ? 'mr-tool-off' : 'mr-tool-on'}`} onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}>
          {muted ? <MicOff size={20} /> : <Mic size={20} />}
        </button>
        <button className={`mr-tool ${videoOff ? 'mr-tool-off' : 'mr-tool-on'}`} onClick={toggleVideo} title={videoOff ? 'Start video' : 'Stop video'}>
          {videoOff ? <VideoOff size={20} /> : <Video size={20} />}
        </button>
        <button className={`mr-tool ${sharing ? 'mr-tool-share-on' : ''}`} onClick={toggleShare} title={sharing ? 'Stop sharing' : 'Share screen'}>
          <MonitorUp size={20} />
        </button>
        {sharing && (
          <button
            className={`mr-tool ${hideSelfView ? 'mr-tool-on' : ''}`}
            onClick={() => setHideSelfView((v) => !v)}
            title={hideSelfView ? 'Show self preview' : 'Hide self preview'}
          >
            {hideSelfView ? <Eye size={20} /> : <EyeOff size={20} />}
          </button>
        )}
        <button
          className={`mr-tool ${showNotes ? 'mr-tool-on' : ''}`}
          onClick={() => { setShowNotes((s) => !s); if (!showNotes) setShowChat(false); }}
          title="Toggle notes"
        >
          <StickyNote size={20} />
        </button>
        <button
          className={`mr-tool ${showChat ? 'mr-tool-on' : ''}`}
          onClick={() => { setShowChat((s) => !s); if (!showChat) setShowNotes(false); }}
          title="Toggle chat"
        >
          <MessageSquare size={20} />
        </button>
        <button className="mr-tool mr-tool-leave" onClick={leave} title="Leave meeting">
          <PhoneOff size={20} />
        </button>
      </div>
    </div>
  );
};

export default MeetingRoomPage;
